/**
 * Pure helpers for client-side merging of captured invoice line items.
 *
 * Used by Sprint B (Inline Merge) of the Invoice Capture Redesign:
 *   feature_planning/invoice_capture_redesign/05_proposed_design.md §"Stream B"
 *
 * Two functions:
 *   - canMergeLineItems(items): { ok, reason? }     pre-flight check
 *   - mergeLineItems(items):    merged item object  pure reshape
 *
 * Conventions follow the AI extraction agent's line-item shape (see
 * backend/invoicing/services/invoice_extraction_agent.py): each item carries
 * item_description, quantity, unit, unit_price, net_amount, vat_amount,
 * gross_amount, vat_rate_percentage, currency.
 *
 * The merge result includes diagnostic fields prefixed with `_merge_` so the
 * UI can render badges/warnings; these are stripped by InvoiceCaptureModal.handleApply
 * before hand-off to the create form.
 */

import { PRICE_DECIMALS, QUANTITY_DECIMALS, formatFigure, lineNet } from '../../../utils/precision';

export const canMergeLineItems = (items) => {
    if (!Array.isArray(items) || items.length < 2) {
        return { ok: false, reason: 'Select at least 2 line items to merge.' };
    }
    const currencies = [...new Set(items.map(i => i.currency).filter(Boolean))];
    if (currencies.length > 1) {
        return {
            ok: false,
            reason: `Cannot merge lines in different currencies (${currencies.join(', ')}). Deselect mismatched lines first.`
        };
    }
    return { ok: true };
};

const pickMode = (values) => {
    const counts = new Map();
    values.forEach(v => {
        if (v === null || v === undefined || v === '') return;
        const key = String(v);
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    if (counts.size === 0) return { value: null, mixed: false };
    let bestKey = null;
    let bestCount = -1;
    for (const [k, c] of counts.entries()) {
        if (c > bestCount) { bestKey = k; bestCount = c; }
    }
    return { value: bestKey, mixed: counts.size > 1 };
};

export const mergeLineItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    if (items.length === 1) return { ...items[0] };

    const sum = (key) => items.reduce((acc, i) => acc + (parseFloat(i[key]) || 0), 0);

    // A float sum of quantities can pick up binary noise (1.1 + 2.2 =
    // 3.3000000000000003) that the quantity column cannot store; bring it back
    // to QUANTITY_DECIMALS so the merged line saves.
    const totalQty = Number(formatFigure(sum('quantity'), 0, QUANTITY_DECIMALS)) || 1;
    const totalNet = sum('net_amount');
    const totalVat = sum('vat_amount');
    const totalGross = sum('gross_amount') || (totalNet + totalVat);

    const unitMode = pickMode(items.map(i => i.unit));
    const vatMode  = pickMode(items.map(i => i.vat_rate_percentage));

    const description = items
        .map(i => (i.item_description || '').trim())
        .filter(Boolean)
        .join('; ');

    // Unit price keeps up to PRICE_DECIMALS, so the merged line re-bills the
    // merged net; net is then quantity x that price to the cent, exactly as the
    // server recomputes it on save.
    const unitPrice = formatFigure(totalNet / totalQty, 2, PRICE_DECIMALS);

    return {
        item_description: description || `Merged from ${items.length} line items`,
        quantity: totalQty,
        unit: unitMode.value,
        unit_price: unitPrice,
        net_amount: lineNet(totalQty, unitPrice),
        vat_rate_percentage: vatMode.value !== null ? vatMode.value : null,
        vat_amount: totalVat.toFixed(2),
        gross_amount: totalGross.toFixed(2),
        currency: items[0].currency || null,
        _merged_from_count: items.length,
        _merge_mixed_unit: unitMode.mixed,
        _merge_mixed_vat: vatMode.mixed,
    };
};
